import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import "@/components/site/PhilosophyStage.css";

const BEATS = [
  { key: "intro", kind: "intro", line: "Every home carries a story" },
  { key: "light", kind: "pillar", word: "Light", supporting: "to stir the first sense of wonder" },
  { key: "material", kind: "pillar", word: "Material", supporting: "to hold memory" },
  { key: "detail", kind: "pillar", word: "Detail", supporting: "to slow the eye" },
  { key: "drama", kind: "pillar", word: "Drama", supporting: "to give the everyday presence" },
  {
    key: "close",
    kind: "close",
    line: ["We design spaces", "for the way life is felt."],
  },
];

const N = BEATS.length;
// Each beat occupies one "slot" in scroll. Desktop keeps 58vh per slot
// (unchanged). Mobile uses a slightly tighter 44vh so a normal touch
// flick visibly advances the reveal instead of feeling stuck.
const SLOT_VH_DESKTOP = 58;
const SLOT_VH_MOBILE = 44;

const lerp = (a, b, t) => a + (b - a) * t;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Smoothstep — gentle ease-in-out, no overshoot.
const smooth = (t) => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

// For one beat, given its float distance from the current position,
// compute the visual state. d = fIndex - i.
//   d ∈ [0, 1)   →  this beat is on-stage, gently dimming.
//   d ∈ [-1, 0) →   this beat is the next preview — a barely-there
//                   upcoming WORD-only thought sitting well below the
//                   active composition (rule + subtext stay hidden).
//   else         →  hidden.
// Returns { style, isActive, isPreviewGhost }.
const beatVisual = (d, i) => {
  if (d >= 0 && d < 1) {
    const f = smooth(d);
    return {
      style: {
        opacity: 1 - f * 0.92,
        transform: `translateY(${-f * 22}px)`,
        filter: `blur(${f * 4}px)`,
      },
      isActive: true,
      isPreviewGhost: false,
    };
  }
  if (d >= -1 && d < 0) {
    const t = smooth(1 + d); // 0 (far below, barely visible) → 1 (taking the stage)
    // The currently-active beat is the one immediately below this preview
    // (index = i - 1). Pillar beats are taller (word + subtext + rule), so
    // we push the preview further down to maintain ≥72px of breathing
    // room below the golden rule.
    const activeKind = BEATS[i - 1]?.kind;
    const restOffset = activeKind === "pillar" ? 296 : 248;
    return {
      style: {
        opacity: lerp(0.04, 1, t),
        transform: `translateY(${lerp(restOffset, 0, t)}px) scale(${lerp(0.94, 1, t)})`,
        filter: `blur(${lerp(2.0, 0, t)}px)`,
      },
      isActive: false,
      // While still mostly-preview, hide subtext + golden rule so the
      // ghost shows only the next word/line. At ~55% rise they begin
      // their own smooth fade-in via CSS.
      isPreviewGhost: t < 0.55,
    };
  }
  return {
    style: { opacity: 0, transform: "translateY(36px)", filter: "blur(6px)" },
    isActive: false,
    isPreviewGhost: false,
  };
};

export default function PhilosophyStage({ id = "philosophy-stage" }) {
  const reducedMotion = useReducedMotion();
  const outerRef = useRef(null);
  const beatRefs = useRef([]);
  // Desktop: floating index (fractional) — drives continuous scroll-
  // scrubbed crossfades + blur + transform via beatVisual().
  // Mobile: discrete activeIndex — only updates when scroll crosses
  // an evenly-spaced threshold; CSS handles the transition. This is
  // what removes the grainy momentum-driven shake on touch.
  const [fIndex, setFIndex] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastActiveRef = useRef(0);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 880px)").matches
      : false
  );
  const [labelIn, setLabelIn] = useState(false);

  // Idle chevron — appears if the user stops scrolling for 3s inside the
  // section, and only until they interact with the section once.
  const [idleHint, setIdleHint] = useState(false);
  const idleTimerRef = useRef(null);
  const interactedRef = useRef(false);

  // Track viewport size — same staged pinned reveal runs on both
  // mobile and desktop now; only the slot size differs.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 880px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // PINNED scroll-driven beat index — runs on every viewport.
  // The section is tall (≈ (N+1)·slot vh); the inner stage stays
  // sticky in the viewport while scroll progress crossfades the beats.
  //
  // IMPORTANT: this is pure native scroll. We never lock body scroll,
  // never preventDefault, never call scrollTo — entering / exiting the
  // section is regular page scroll. Only the on-stage beat changes.
  useEffect(() => {
    if (reducedMotion) return;
    const el = outerRef.current;
    if (!el) return;

    let rafId = null;

    const armIdleHint = (insideSection) => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (!insideSection || interactedRef.current) {
        setIdleHint(false);
        return;
      }
      idleTimerRef.current = setTimeout(() => {
        if (!interactedRef.current) setIdleHint(true);
      }, 3000);
    };

    const update = () => {
      rafId = null;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const scrolled = Math.max(0, -rect.top);
      const total = Math.max(1, rect.height - vh);
      const progress = clamp(scrolled / total, 0, 1);

      if (isMobile) {
        // Discrete staged transitions — every beat occupies an equal
        // slot of progress. Only flip activeIndex when the threshold
        // is crossed; never write fractional state on every frame.
        const next = clamp(Math.floor(progress * N), 0, N - 1);
        if (next !== lastActiveRef.current) {
          lastActiveRef.current = next;
          setActiveIndex(next);
        }
      } else {
        // Desktop: smooth scroll-scrubbed float across all beats.
        const f = progress * (N - 1);
        setFIndex(f);
      }

      if (rect.top < vh * 0.85) setLabelIn(true);

      // Idle chevron — only while section is meaningfully in viewport.
      const inside = rect.top <= 0 && rect.bottom > vh;
      if (inside && !interactedRef.current && progress > 0.02) {
        interactedRef.current = true;
        setIdleHint(false);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      } else {
        armIdleHint(inside);
      }
    };

    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [reducedMotion, isMobile]);

  // Reduced motion: show everything immediately
  useEffect(() => {
    if (!reducedMotion) return;
    setLabelIn(true);
    const els = beatRefs.current.filter(Boolean);
    els.forEach((n) => n.classList.add("is-active"));
  }, [reducedMotion]);

  const pinned = !reducedMotion;
  const slotVh = isMobile ? SLOT_VH_MOBILE : SLOT_VH_DESKTOP;
  const pinnedHeightVh = (N + 1) * slotVh;
  const sectionStyle = pinned ? { height: `${pinnedHeightVh}vh` } : undefined;

  return (
    <section
      ref={outerRef}
      id={id}
      className={[
        "phil-stage",
        pinned ? "phil-stage--pinned" : "phil-stage--reduced",
      ].join(" ")}
      style={sectionStyle}
      data-testid="philosophy-section"
      aria-label="Philosophy"
    >
      <div className="phil-stage__sticky">
        <div className="phil-stage__paper" aria-hidden="true" />
        <div className="phil-stage__glow" aria-hidden="true" />

        <div className="phil-stage__inner">
          <div
            className={["phil-stage__label-row", labelIn ? "is-entered" : ""].join(" ")}
            data-testid="philosophy-label"
          >
            <span className="phil-stage__dot" aria-hidden="true" />
            <span className="phil-stage__label">Philosophy</span>
          </div>

          <div className="phil-stage__stage" aria-live="polite">
            {BEATS.map((b, i) => {
              // On mobile we render discrete staged transitions:
              // CSS handles the fade via the .is-active class, with
              // no per-frame inline-style updates and no ghost
              // preview text behind the active beat. Desktop keeps
              // the existing scroll-scrubbed inline-style render.
              let style;
              let isActive;
              let isPreviewGhost;
              if (!pinned) {
                style = undefined;
                isActive = false;
                isPreviewGhost = false;
              } else if (isMobile) {
                style = undefined;
                isActive = i === activeIndex;
                isPreviewGhost = false;
              } else {
                const v = beatVisual(fIndex - i, i);
                style = v.style;
                isActive = v.isActive;
                isPreviewGhost = v.isPreviewGhost;
              }
              return (
                <div
                  key={b.key}
                  ref={(n) => (beatRefs.current[i] = n)}
                  className={[
                    "phil-beat",
                    `phil-beat--${b.kind}`,
                    pinned && isActive ? "is-active" : "",
                    pinned && isPreviewGhost ? "is-preview-ghost" : "",
                  ].join(" ")}
                  style={style}
                  data-testid={`philosophy-beat-${b.key}`}
                  aria-hidden={pinned ? !isActive : undefined}
                >
                  {b.kind === "pillar" ? (
                    <>
                      <h3 className="phil-beat__word">{b.word}</h3>
                      <p className="phil-beat__supporting">{b.supporting}</p>
                    </>
                  ) : (
                    <p className="phil-beat__line">
                      {Array.isArray(b.line)
                        ? b.line.map((part, idx) => (
                            <span key={idx}>
                              {part}
                              {idx < b.line.length - 1 ? <br /> : null}
                            </span>
                          ))
                        : b.line}
                    </p>
                  )}
                  <span className="phil-beat__rule" aria-hidden="true" />
                </div>
              );
            })}
          </div>
        </div>

        {/* Idle scroll hint — a single, very small chevron that appears
            only after 3s of no interaction inside the section, and never
            again once the user has scrolled within it. */}
        {pinned && (
          <div
            className={["phil-stage__idle", idleHint ? "is-visible" : ""].join(" ")}
            aria-hidden="true"
            data-testid="philosophy-idle-cue"
          >
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              focusable="false"
            >
              <path
                d="M1 1L6 5L11 1"
                stroke="currentColor"
                strokeWidth="1"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </div>
    </section>
  );
}
